import React, { useEffect, useRef, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom pulsing marker icon
const createPulsingIcon = () => L.divIcon({
  className: 'gps-pulse-marker',
  html: `<div class="pulse-dot"></div><div class="pulse-ring"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Auto-pan map when position changes
const MapUpdater = ({ position, follow }) => {
  const map = useMap();
  useEffect(() => {
    if (position && follow) {
      map.setView(position, map.getZoom(), { animate: true, duration: 0.5 });
    }
  }, [position, follow, map]);
  return null;
};

const MAX_TRAIL_POINTS = 500;
const MIN_DISTANCE_METERS = 2; // minimum distance to add new trail point

// Haversine distance in meters
const getDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const GPSMap = ({ gpsData, unitName }) => {
  const [trail, setTrail] = useState([]);
  const [followMode, setFollowMode] = useState(true);
  const lastPoint = useRef(null);
  const pulsingIcon = useMemo(() => createPulsingIcon(), []);

  // Update trail when GPS changes
  useEffect(() => {
    if (!gpsData || gpsData.error || gpsData.lat == null || gpsData.lng == null) return;

    const newPoint = [gpsData.lat, gpsData.lng];

    // Check minimum distance
    if (lastPoint.current) {
      const dist = getDistance(lastPoint.current[0], lastPoint.current[1], newPoint[0], newPoint[1]);
      if (dist < MIN_DISTANCE_METERS) return;
    }

    lastPoint.current = newPoint;
    setTrail(prev => {
      const updated = [...prev, newPoint];
      return updated.length > MAX_TRAIL_POINTS ? updated.slice(-MAX_TRAIL_POINTS) : updated;
    });
  }, [gpsData]);

  const hasGPS = gpsData && !gpsData.error && gpsData.lat != null && gpsData.lng != null;
  const currentPos = hasGPS ? [gpsData.lat, gpsData.lng] : null;

  if (!hasGPS && trail.length === 0) {
    return (
      <div className="gps-map-container gps-map-empty">
        <div className="gps-empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
        </div>
        <div className="gps-empty-text">Awaiting GPS Signal...</div>
        <div className="gps-empty-sub">Location will appear when GPS is available</div>
      </div>
    );
  }

  const center = currentPos || trail[trail.length - 1] || [13.7563, 100.5018]; // Default Bangkok

  return (
    <div className="gps-map-container">
      <div className="gps-map-header">
        <div className="gps-map-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
          GPS Tracking {unitName && `— ${unitName}`}
        </div>
        <div className="gps-map-controls">
          {hasGPS && (
            <span className="gps-coord-badge">
              {gpsData.lat.toFixed(5)}, {gpsData.lng.toFixed(5)}
            </span>
          )}
          <button
            className={`gps-follow-btn ${followMode ? 'active' : ''}`}
            onClick={() => setFollowMode(f => !f)}
            title={followMode ? 'Free move' : 'Follow unit'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={followMode ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
            </svg>
          </button>
          {trail.length > 1 && (
            <button
              className="gps-clear-btn"
              onClick={() => { setTrail(currentPos ? [currentPos] : []); }}
              title="Clear trail"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="gps-map-wrapper">
        <MapContainer
          center={center}
          zoom={17}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapUpdater position={currentPos} follow={followMode} />

          {/* Movement trail */}
          {trail.length > 1 && (
            <Polyline
              positions={trail}
              pathOptions={{ color: '#3b82f6', weight: 3, opacity: 0.7, dashArray: '6,4' }}
            />
          )}

          {/* Current position */}
          {currentPos && (
            <Marker position={currentPos} icon={pulsingIcon} />
          )}
        </MapContainer>

        {/* Trail info overlay */}
        <div className="gps-map-overlay">
          {trail.length > 1 && (
            <span className="gps-trail-badge">{trail.length} points</span>
          )}
          {gpsData?.accuracy && (
            <span className="gps-accuracy-badge">±{Math.round(gpsData.accuracy)}m</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles
export const GPSMapStyles = () => (
  <style>{`
    @import url('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css');

    .gps-map-container {
      background: #fff;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 200px;
    }
    .gps-map-empty {
      align-items: center;
      justify-content: center;
      padding: 24px;
      gap: 8px;
    }
    .gps-empty-icon {
      width: 56px; height: 56px;
      border-radius: 50%;
      background: #f1f5f9;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 4px;
    }
    .gps-empty-text {
      font-size: 14px; font-weight: 600; color: #475569;
    }
    .gps-empty-sub {
      font-size: 11px; color: #94a3b8;
    }
    .gps-map-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid #f1f5f9;
      background: #fafbfc;
      gap: 8px;
      flex-wrap: wrap;
    }
    .gps-map-title {
      font-size: 12px;
      font-weight: 600;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .gps-map-controls {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .gps-coord-badge {
      font-size: 10px;
      font-family: monospace;
      background: #f1f5f9;
      color: #475569;
      padding: 2px 8px;
      border-radius: 10px;
    }
    .gps-follow-btn, .gps-clear-btn {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 3px 8px;
      font-size: 11px;
      cursor: pointer;
      background: #fff;
      color: #64748b;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .gps-follow-btn:hover, .gps-clear-btn:hover {
      background: #f1f5f9;
    }
    .gps-follow-btn.active {
      background: #3b82f6;
      color: white;
      border-color: #3b82f6;
    }
    .gps-map-wrapper {
      flex: 1;
      position: relative;
      min-height: 180px;
    }
    .gps-map-wrapper .leaflet-container {
      border-radius: 0 0 12px 12px;
    }
    .gps-map-overlay {
      position: absolute;
      bottom: 8px;
      left: 8px;
      display: flex;
      gap: 4px;
      z-index: 1000;
    }
    .gps-trail-badge, .gps-accuracy-badge {
      font-size: 10px;
      padding: 2px 8px;
      border-radius: 10px;
      background: rgba(255,255,255,0.9);
      color: #475569;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(0,0,0,0.08);
    }

    /* Pulsing marker */
    .gps-pulse-marker {
      position: relative;
    }
    .pulse-dot {
      width: 12px; height: 12px;
      background: #3b82f6;
      border: 2px solid white;
      border-radius: 50%;
      position: absolute;
      top: 4px; left: 4px;
      box-shadow: 0 1px 4px rgba(59,130,246,0.5);
    }
    .pulse-ring {
      width: 20px; height: 20px;
      border: 2px solid #3b82f6;
      border-radius: 50%;
      position: absolute;
      top: 0; left: 0;
      animation: gps-pulse 2s ease-out infinite;
      opacity: 0;
    }
    @keyframes gps-pulse {
      0% { transform: scale(1); opacity: 0.6; }
      100% { transform: scale(2.5); opacity: 0; }
    }

    /* Responsive */
    .gps-map-container { min-width: 0; }
    .gps-map-title { min-width: 0; overflow-wrap: anywhere; }
    @media (max-width: 1100px) {
      .gps-map-wrapper { min-height: 240px; }
    }
    @media (max-width: 639px) {
      .gps-map-container { min-height: 0; }
      .gps-map-wrapper { min-height: 220px; height: 52vw; max-height: 300px; }
    }
    @media (pointer: coarse) {
      .gps-follow-btn, .gps-clear-btn {
        min-height: 44px;
        min-width: 44px;
        justify-content: center;
        padding: 6px 12px;
      }
    }
  `}</style>
);
